
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useUpdateTimeSlot, TimeSlot } from "@/hooks/useTimeSlots";
import { format } from "date-fns";
import { User } from "lucide-react";

interface EditTimeSlotDialogProps {
  timeSlot: TimeSlot | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface EditTimeSlotFormData {
  max_bookings: number;
  womens_only: boolean;
}

const EditTimeSlotDialog = ({ timeSlot, open, onOpenChange }: EditTimeSlotDialogProps) => {
  const updateTimeSlot = useUpdateTimeSlot();
  
  const form = useForm<EditTimeSlotFormData>({
    defaultValues: {
      max_bookings: timeSlot?.max_bookings || 1,
      womens_only: timeSlot?.womens_only || false,
    },
  });

  // Reset form when timeSlot changes
  useEffect(() => {
    if (timeSlot) {
      form.reset({
        max_bookings: timeSlot.max_bookings,
        womens_only: timeSlot.womens_only,
      });
    }
  }, [timeSlot, form]);

  const onSubmit = async (data: EditTimeSlotFormData) => {
    if (!timeSlot) return;

    try {
      await updateTimeSlot.mutateAsync({
        id: timeSlot.id,
        max_bookings: data.max_bookings,
        womens_only: data.womens_only,
      });

      onOpenChange(false);
    } catch (error) {
      console.error('Error updating time slot:', error);
    }
  };

  if (!timeSlot) return null;

  const startTime = new Date(timeSlot.start_time);
  const endTime = new Date(timeSlot.end_time);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Time Slot</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Read-only fields */}
          <div className="space-y-2">
            <Label>Service</Label>
            <Input 
              value={timeSlot.service?.name || 'N/A'} 
              readOnly 
              className="bg-gray-50" 
            />
          </div>

          <div className="space-y-2">
            <Label>Date</Label>
            <Input 
              value={format(startTime, "MMM dd, yyyy")} 
              readOnly 
              className="bg-gray-50" 
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Time</Label>
              <Input 
                value={format(startTime, "HH:mm")} 
                readOnly 
                className="bg-gray-50" 
              />
            </div>
            <div className="space-y-2">
              <Label>End Time</Label>
              <Input 
                value={format(endTime, "HH:mm")} 
                readOnly 
                className="bg-gray-50" 
              />
            </div>
          </div>

          {timeSlot.room && (
            <div className="space-y-2">
              <Label>Room</Label>
              <Input 
                value={timeSlot.room.name} 
                readOnly 
                className="bg-gray-50" 
              />
            </div>
          )}

          {/* Editable fields */}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="max_bookings"
                rules={{ 
                  required: "Please enter max bookings",
                  min: { value: 1, message: "Must be at least 1" },
                  max: { value: 100, message: "Cannot exceed 100" }
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Maximum Bookings (Capacity)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        min="1" 
                        max="100"
                        {...field} 
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                      />
                    </FormControl>
                    <FormMessage />
                    <div className="text-sm text-gray-600">
                      Current bookings: {timeSlot.current_bookings}
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="womens_only"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between p-3 border rounded-lg">
                    <div className="space-y-0.5">
                      <FormLabel className="text-sm font-medium flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Women Only Time Slot
                      </FormLabel>
                      <p className="text-xs text-gray-500">
                        Restrict this time slot to women only
                      </p>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={updateTimeSlot.isPending}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="flex justify-end space-x-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={updateTimeSlot.isPending}
                >
                  {updateTimeSlot.isPending ? "Updating..." : "Update Time Slot"}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditTimeSlotDialog;
